/* eslint-disable react-hooks/exhaustive-deps */

import { type FC, useCallback, useContext, useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import {
  CheckCircle,
  Envelope,
  GeoAlt,
  PinMap,
  Plus,
  Trash3Fill,
  Whatsapp,
  Wifi,
} from 'react-bootstrap-icons';
import { FormProvider, useForm } from 'react-hook-form';

import './styles/styles.css';

import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { Badge, Button, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  coachHouseCellphone,
  coachHouseContactPhone,
  ourStoryContent,
} from '../../constants/generalData';
import { GuestPortalContext } from '../../modules/context/guestPortalContextV2';
import { PackageStatus } from '../../modules/enums/status';
import {
  type ErrorEnvelop,
  useGuestAuth,
  useManualQueryRequest,
  useMutationRequest,
  useNotifications,
} from '../../modules/hooks';
import { useTransaction } from '../../modules/hooks/useTransaction';
import type { GuestPackage, PackagesResponse } from '../../modules/interfaces';
import { guestPortalLogger } from '../../modules/logger/loggers';
import { endpoints } from '../../modules/mappers/urls';
import { getTransactionContext } from '../../modules/transaction';
import { formatDateTime } from '../../modules/utils/dateFormat';
import { formatPropertyAddressPortal } from '../../modules/utils/formatAddress';
import RenderHTMLContent from '../../modules/utils/renderHTMLcontent';
import { openSMSApp } from '../../modules/utils/sendMessage';
import {
  getApiErrorMessage,
  getApiRequestId,
  getSuccessRequestId,
} from '../../modules/utils/transform';
import { updateOrCreatePackageList } from '../../modules/utils/updateOrCreateList';
import { CheckInCard } from '../Cards/CheckInCardV2';
import { ConfirmModal, type ModalState } from '../Modals/Confirm';
import { CustomizeStay, ParkingGuideModal } from '../Modals/GuestPagesModalsV2';
import { ConfirmStepsWarningModal } from '../Modals/GuestPagesModalsV2/ConfirmWarningModal';
import { GuestPortalErrors } from '../Modals/GuestPagesModalsV2/constants/GuestPortalConstants';
import { CardForPayPackagesModal } from '../Modals/PackagesModals/CardForPayPackagesModalV2';
import PayPackagesQuestionModal from '../Modals/PackagesModals/PayPackagesQuestionModalV2';
import { useGetAccessCode } from './endpoints/GuestPageEnpoints';
import { changeFavicon } from './utils';

export const GuestPortal: FC = () => {
  const location = useLocation();
  const [customizeStayModal, setCustomizeStayModal] = useState(false);
  const [roomCode, setRoomCode] = useState('');
  const [guestId, setGuestId] = useState('');
  const { token, generateAuthToken } = useGuestAuth()!;
  const { reservationId } = useParams();
  const [packages, setPackages] = useState<PackagesResponse[]>([]);
  const [packageData, setPackageData] = useState<PackagesResponse>();
  const [guestPackages, setGuestPackages] = useState<GuestPackage[]>([]);
  const [showAnotherCardModal, setShowAnotherCardModal] = useState(false);
  const [showPackagesSuccessModal, setShowPackagesSuccessModal] = useState(false);
  const [questionModal, setQuestionModal] = useState(false);
  const { setSimpleToasts } = useNotifications()!;
  const [packagesWarningModal, setPackagesWarningModal] = useState(false);
  const [showParkingGuide, setShowParkingGuide] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ModalState>({
    show: false,
    title: '',
    body: '',
  });

  const { startTransaction, endTransaction } = useTransaction();

  const methods = useForm<GuestPackage>({
    defaultValues: {
      price: 0,
      quantity: 1,
      reservationId: '',
      packageId: '',
      amount: 0,
      size: '',
    },
    mode: 'onChange',
  });

  const navigate = useNavigate();
  const {
    reservation,
    getMewsReservations,
    loadingReservation,
    preregisteredGuest,
    getGuestPreregistered,
    loading,
    addPackageResult,
    addPackageToGuest,
    loadingAddPackage,
    addPackageError,
    cardInfo,
    getCardInfo,
  } = useContext(GuestPortalContext)!;
  const [maxTitleLength, setMaxTitleLength] = useState(55);

  const checkoutURL = `${endpoints.MEWS_RESERVATIONS}/check-out`;
  const [
    { data: checkoutData, loading: loadingCheckout, error: guestError },
    checkOutReservation,
  ] = useMutationRequest(
    checkoutURL,
    'post',
    {
      authGuestToken: token,
    },
    { manual: true },
  );

  const closeConfirmModal = () => {
    setConfirmModal({
      ...confirmModal,
      show: false,
    });
  };
  const openConfirmModal = () => {
    setConfirmModal({
      title: 'Confirm Check Out',
      body: (
        <span>
          <b>Property access code will cease to work once Check Out is done</b>. <br />{' '}
          <br /> Are you sure you want to proceed with the Check Out?
        </span>
      ),
      show: true,
    });
  };

  const handleConfirmModal = useCallback(() => {
    if (loadingCheckout) return;
    startTransaction('check-out');
    guestPortalLogger.info(
      { ...getTransactionContext(), reservationId },
      'Check-out initiated',
    );
    setConfirmModal({
      ...confirmModal,
      show: false,
    });
    checkOutReservation({ reservationId });
  }, [confirmModal, setConfirmModal, checkOutReservation, startTransaction]);

  const [
    { data: accessCodeResult, loading: loadingAccessCode },
    getReservationAccessCode,
  ] = useGetAccessCode(reservationId || '');

  const [{ data, loading: loadingPackages, error }, getPackages] = useManualQueryRequest<
    PackagesResponse[]
  >(
    endpoints.PACKAGES,
    {
      authGuestToken: token,
      propertyId: reservation?.Property?.uuid,
      params: { status: PackageStatus.ACTIVE },
    },
    { manual: true },
  );

  useEffect(() => {
    if (loadingCheckout) return;

    if (checkoutData) {
      guestPortalLogger.info(
        {
          ...getTransactionContext(),
          reservationId,
          requestId: getSuccessRequestId(checkoutData),
        },
        'Check-out succeeded',
      );
      endTransaction();
      getMewsReservations({ extended: true });
    }

    if (guestError && guestError.message) {
      const axiosError = guestError as AxiosError<ErrorEnvelop>;
      const message = getApiErrorMessage(axiosError);
      guestPortalLogger.error(
        {
          ...getTransactionContext(),
          reservationId,
          error: message,
          requestId: getApiRequestId(axiosError),
        },
        'Check-out failed',
      );
      endTransaction();

      if (message.includes('too early') && message.includes('check-out')) {
        setSimpleToasts({
          message:
            'It’s too early to check-out. If you are looking to leave ahead of your scheduled check-out date, please reach out to your personal concierge. Thank you!',
          type: 'warning',
          show: true,
        });
      } else {
        setSimpleToasts({
          message:
            'Whoops! Please refresh the page. If this message reappears after refreshing, please reach out to your personal concierge for further assistance. Thank you!',
          type: 'danger',
          show: true,
        });
      }
    }
  }, [loadingCheckout, checkoutData, guestError]);

  useEffect(() => {
    if (!token) return;

    getGuestPreregistered();
  }, [token]);

  useEffect(() => {
    if (!reservation?.Property?.uuid) return;

    const property = reservation?.Property?.logoUrl;
    if (property) {
      changeFavicon(property);
    }

    getPackages();
  }, [reservation?.Property]);

  // If a guest has already registered, shows access code or redirect to feedback view
  useEffect(() => {
    if (!preregisteredGuest) return;

    if (reservation?.EndUtc) {
      const now = DateTime.now();
      const reservationEnd = DateTime.fromISO(reservation?.EndUtc || '');

      if (now > reservationEnd || reservation.State === 'Processed') {
        navigate(`/feedback/${reservationId}`, {
          state: { guestEmail: reservation?.Customer?.Email },
        });
        return;
      }
    }

    setGuestId(preregisteredGuest.guest.uuid || '');

    getReservationAccessCode();
  }, [preregisteredGuest, navigate, reservationId, reservation?.EndUtc]);

  useEffect(() => {
    if (!error) return;

    const errorMessage = getApiErrorMessage(error as AxiosError<ErrorEnvelop>);

    if (errorMessage === GuestPortalErrors.JWT_EXPIRED) {
      setSimpleToasts({
        message: 'Token expired. Please wait while we refresh it.',
        type: 'warning',
        show: true,
      });

      generateAuthToken({ reservationId });
    }
  }, [error]);

  // Set room code after guest registration
  useEffect(() => {
    if (loadingAccessCode || !accessCodeResult) return;
    setRoomCode(String(accessCodeResult.result));
  }, [accessCodeResult, getReservationAccessCode, loadingAccessCode]);

  useEffect(() => {
    if (!location?.state?.roomCode) return;

    setRoomCode(location?.state?.roomCode);
    getMewsReservations({ extended: true });

    if (location?.state?.customerId) {
      getCardInfo({ customerId: location.state.customerId });
    }

    if (location?.state?.packages && location?.state?.packages.length) {
      setPackagesWarningModal(true);
      setGuestPackages(location?.state?.packages as GuestPackage[]);
    }
  }, [location]);

  useEffect(() => {
    if (loadingPackages || !reservation?.StartUtc) return;

    const validPackages: PackagesResponse[] =
      data?.result.reduce((accumulator: PackagesResponse[], item) => {
        if (!item.params) {
          return [...accumulator, item];
        }

        const advanceNotice = item.params.find(
          (value) => value.type === 'ADVANCED NOTICE',
        );

        if (advanceNotice) {
          const startDate = DateTime.fromISO(reservation?.StartUtc);

          const [amount, unit] = advanceNotice.values.split(' ');
          const advancedNoticeDate = startDate.minus({
            [unit]: parseInt(amount, 10),
          });

          const currentDate = DateTime.now();

          if (currentDate < advancedNoticeDate) {
            return [...accumulator, item];
          }
        } else {
          return [...accumulator, item];
        }

        return accumulator;
      }, []) || [];

    setPackages(validPackages);
  }, [data?.result, loadingPackages, reservation?.StartUtc, preregisteredGuest]);

  useEffect(() => {
    if (!methods.getValues('price') || customizeStayModal) return;

    const guestPageList = updateOrCreatePackageList(guestPackages, methods.getValues());

    setGuestPackages(guestPageList);

    methods.reset({
      price: 0,
      quantity: 1,
      reservationId: '',
      packageId: '',
      amount: 0,
      size: '',
    });
  }, [methods, customizeStayModal]);

  const onSubmit = () => {
    if (loadingAddPackage) return;
    startTransaction('request-packages');
    guestPortalLogger.info(
      { ...getTransactionContext(), reservationId },
      'Request packages initiated',
    );
    addPackageToGuest({
      reservationId,
      packages: guestPackages || [],
      guestId,
    });
  };

  useEffect(() => {
    if (!addPackageResult && !addPackageError) return;

    if (addPackageError) {
      const axiosError = addPackageError as AxiosError<ErrorEnvelop>;
      const message = getApiErrorMessage(axiosError);
      guestPortalLogger.error(
        {
          ...getTransactionContext(),
          reservationId,
          error: message,
          requestId: getApiRequestId(axiosError),
        },
        'Request packages failed',
      );
      endTransaction();
      setSimpleToasts({
        message,
        type: 'danger',
        show: true,
      });
    }

    setShowPackagesSuccessModal(true);

    if (addPackageResult) {
      guestPortalLogger.info(
        {
          ...getTransactionContext(),
          reservationId,
          requestId: getSuccessRequestId(addPackageResult),
        },
        'Request packages succeeded',
      );
      endTransaction();
      getGuestPreregistered();
      setGuestPackages([]);

      setSimpleToasts({
        message: 'Add-on requested successfully',
        type: 'success',
        show: true,
      });
    }
  }, [addPackageResult]);

  const showPackageModalHandler = (item: PackagesResponse) => {
    setPackageData(item);

    const updatePackage = guestPackages.find(
      (packageItem) => packageItem?.packageId === item.uuid,
    );

    if (updatePackage) {
      methods.reset({
        price: updatePackage.price,
        quantity: updatePackage.quantity,
        reservationId: updatePackage.reservationId,
        packageId: updatePackage.packageId,
        amount: updatePackage.amount,
        size: updatePackage?.size || '',
      });
    }

    setCustomizeStayModal(true);
  };

  const deleteGuestPackage = (item: PackagesResponse) => {
    const values = guestPackages.filter(({ packageId }) => packageId !== item.uuid);
    setGuestPackages(values);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 770) {
        setMaxTitleLength(30);
      }
      if (window.innerWidth < 770) {
        setMaxTitleLength(20);
      }
      if (window.innerWidth < 400) {
        setMaxTitleLength(14);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <FormProvider {...methods}>
      <Container className="container-paddings">
        <hr
          style={{
            marginTop: '45px',
          }}
        />
      </Container>
      <Container
        className="container-paddings direction-padding"
        style={{ height: '4.5rem' }}
      >
        <Row style={{ paddingLeft: '0px', paddingRight: '0px' }}>
          <Col
            xs={7}
            style={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
            }}
          >
            <GeoAlt size={26} />
            <div className="direction-title">
              {formatPropertyAddressPortal(reservation?.Property)}
            </div>
          </Col>
          <Col
            xs={5}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'right',
            }}
          >
            <Envelope
              onClick={() =>
                openSMSApp(reservation?.Property?.cellphoneNumber || coachHouseCellphone)
              }
              title="Message us"
              size={26}
              className="message-button-icon"
            />

            <Whatsapp
              onClick={(e) => {
                window.location.href = `tel:${
                  reservation?.Property?.phoneNumber || coachHouseContactPhone
                }`;
                e.preventDefault();
              }}
              title="Call us"
              size={26}
              className="message-button-icon"
              style={{ marginLeft: '30px' }}
            />
          </Col>
        </Row>
      </Container>
      <Container
        className="container-paddings direction-padding"
        style={{ height: '4.5rem', marginTop: '5px' }}
      >
        <Row style={{ paddingLeft: '0px', paddingRight: '0px' }}>
          <Col className="network-align">
            <Wifi size={30} />
            <div style={{ marginLeft: '15px' }}>
              <div className="network-title">Wifi Network</div>
              <div style={{ fontSize: '14px' }}>
                {loading
                  ? 'Loading password...'
                  : reservation?.Room?.wifiName || 'Wi-Fi Network Not Available'}
              </div>
            </div>
          </Col>
          <Col className="password-align">
            <div>
              <div className="password-title">Password</div>
              <div style={{ fontSize: '14px' }}>
                {loading
                  ? 'Loading password...'
                  : reservation?.Room?.wifiPassword || 'Wi-Fi Password Not Available'}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      {reservation?.Property?.parkingGuideUrl && (
        <>
          <Container
            className="container-paddings direction-padding"
            style={{ height: '5.5rem', marginTop: '5px', cursor: 'pointer' }}
            onClick={() => setShowParkingGuide(true)}
          >
            <Row>
              <Col className="network-align">
                <PinMap size={30} />
                <div style={{ marginLeft: '15px' }}>
                  <div className="network-title">Parking Guide</div>
                  <div style={{ fontSize: '14px' }}>Tap to view parking options</div>
                </div>
              </Col>
            </Row>
          </Container>
          <ParkingGuideModal
            show={showParkingGuide}
            handleClose={() => setShowParkingGuide(false)}
            parkingGuideUrl={reservation.Property.parkingGuideUrl}
          />
        </>
      )}
      <Container>
        <Row>
          <div className="table-title ">
            <h6
              className="mb-0 warning-title"
              style={{ color: 'var(--secondary-font-color, #979797)' }}
            >
              Your door code will not become active until your scheduled check-in time
            </h6>
          </div>
        </Row>
      </Container>
      <Container
        style={{
          width: '100%',
          paddingLeft: '0px',
          paddingRight: '0px',
          backgroundColor: 'var(--access-code-bg-color, #fff)',
          marginTop: '12px',
        }}
      >
        <CheckInCard
          title={roomCode === '' ? 'Check-In' : 'Your Door Code'}
          content={
            roomCode === '' ? (
              <span className="reservation-date">
                {formatDateTime(reservation?.StartUtc || '')}
              </span>
            ) : (
              <h4 className="room-code">{`${roomCode}#`}</h4>
            )
          }
          active={!reservation?.InternalReservation?.isVerified}
          data={guestPackages}
        />
        <hr
          style={{
            margin: 0,
            height: '1px',
            border: 'none',
            backgroundColor: 'var(--access-code-border-color, #979797)',
            marginLeft: '15px',
          }}
        />
        <CheckInCard
          title="Check-out"
          content={
            <span className="reservation-date">
              {formatDateTime(reservation?.EndUtc || '')}
            </span>
          }
          type="checkOut"
          active={reservation?.InternalReservation?.isVerified}
          checkOutFunction={openConfirmModal}
          titleCheckIn={roomCode ? 'Check-In' : ''}
          contentCheckIn={
            roomCode ? (
              <span className="reservation-date">
                {formatDateTime(reservation?.StartUtc || '')}
              </span>
            ) : (
              ''
            )
          }
          disabled={loadingCheckout || confirmModal.show || loadingReservation}
        />
      </Container>

      <Container className="container-paddings">
        <Row style={{ marginTop: '24px' }}>
          <Col xs="6">
            <div className="table-title">
              <h6
                className="mb-0"
                style={{ color: 'var(--secondary-font-color, #979797)' }}
              >
                Customize Your Stay
              </h6>
            </div>
          </Col>
        </Row>

        {preregisteredGuest && guestPackages.length > 0 && (
          <Row style={{ marginTop: '10px', marginBottom: '10px' }}>
            <Col style={{ display: 'flex', justifyContent: 'end' }}>
              <Button
                onClick={() =>
                  cardInfo &&
                  cardInfo.last4 &&
                  preregisteredGuest &&
                  preregisteredGuest.stripeId
                    ? setQuestionModal(true)
                    : setShowAnotherCardModal(true)
                }
                className="save-button"
                disabled={
                  loadingAddPackage || loading || showAnotherCardModal || questionModal
                }
              >
                <Plus size={20} />
                Request Add-ons
              </Button>
            </Col>
          </Row>
        )}

        <div className="scroll-portal">
          <Row>
            {packages?.map((item) => (
              <Col key={item.uuid} xs="4" lg="4" style={{ marginTop: '10px' }}>
                {guestPackages.find(
                  (packageItem) => packageItem?.packageId === item.uuid,
                ) && (
                  <Row>
                    <Col>
                      <Badge className="quantity-badge" bg="danger">
                        {guestPackages.find(
                          (packageItem) => packageItem?.packageId === item.uuid,
                        )?.quantity || ''}
                      </Badge>
                    </Col>
                    <Col style={{ display: 'flex', justifyContent: 'end' }}>
                      <OverlayTrigger
                        overlay={<Tooltip>Remove this package from the list</Tooltip>}
                      >
                        <Badge
                          className="notify-badge"
                          bg="danger"
                          onClick={() => deleteGuestPackage(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Trash3Fill size={18} />
                        </Badge>
                      </OverlayTrigger>
                    </Col>
                  </Row>
                )}
                <Container
                  onClick={() => showPackageModalHandler(item)}
                  className="package-container"
                >
                  {item?.media?.[0]?.uri ? (
                    <Image
                      alt=""
                      src={item?.media?.[0].uri}
                      className={
                        guestPackages.find(
                          (packageItem) => packageItem?.packageId === item.uuid,
                        )
                          ? 'd-inline-block align-top customize-stay-selected-container'
                          : 'd-inline-block align-top customize-stay-container'
                      }
                    />
                  ) : (
                    <Image
                      alt=""
                      src="/no_image.jpg"
                      className={
                        guestPackages.find(
                          (packageItem) => packageItem?.packageId === item.uuid,
                        )
                          ? 'd-inline-block align-top customize-stay-selected-container'
                          : 'd-inline-block align-top customize-stay-container'
                      }
                    />
                  )}
                  {item.name && item?.name?.length < 25 ? (
                    <h6 className="customize-stay-subtitle">{item.name}</h6>
                  ) : (
                    <OverlayTrigger overlay={<Tooltip>{item.name}</Tooltip>}>
                      <h6 className="customize-stay-subtitle">
                        {item?.name?.substring(0, maxTitleLength)} {' ...'}
                      </h6>
                    </OverlayTrigger>
                  )}
                </Container>
              </Col>
            ))}
          </Row>
        </div>
      </Container>

      <Container className="container-paddings">
        <Row className="mt-16px">
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>About Your Room</Accordion.Header>
              <Accordion.Body>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {reservation?.Room?.description ||
                    'We are working on a description about your room.'}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>View Check-In Steps</Accordion.Header>
              <Accordion.Body>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {reservation?.Room?.checkInSteps ||
                    'We are working to describe the check-in steps'}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Our Story</Accordion.Header>
              <Accordion.Body>
                <RenderHTMLContent
                  content={reservation?.Property?.ourStoryContent || ourStoryContent}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Row>
      </Container>

      {preregisteredGuest && preregisteredGuest.stripeId && (
        <CardForPayPackagesModal
          show={showAnotherCardModal}
          handleClose={() => {
            setShowAnotherCardModal(false);
            setQuestionModal(false);
          }}
          customerId={preregisteredGuest.stripeId}
          reservationId={reservationId || ''}
          onRequestSubmit={onSubmit}
        />
      )}

      {preregisteredGuest &&
        preregisteredGuest.stripeId &&
        cardInfo &&
        cardInfo.last4 && (
          <PayPackagesQuestionModal
            show={questionModal}
            onConfirm={() => {
              setShowAnotherCardModal(true);
            }}
            onHide={() => {
              setQuestionModal(false);
              onSubmit();
            }}
            last4={cardInfo.last4}
          />
        )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        body={confirmModal.body}
        handleCancel={closeConfirmModal}
        handleConfirm={handleConfirmModal}
        isGuestPortal
      />
      {packageData && (
        <CustomizeStay
          show={customizeStayModal}
          handleClose={() => {
            setCustomizeStayModal(false);
          }}
          packageItem={packageData}
        />
      )}

      <ConfirmStepsWarningModal
        show={packagesWarningModal}
        handleClose={() => {
          setPackagesWarningModal(false);
        }}
        title="You Have Pending Add-Ons"
        text={
          'It looks like some add-ons were selected, but they haven\'t been confirmed yet. Be sure to tap "Request Add-Ons" to finalize them!'
        }
      />

      <ConfirmStepsWarningModal
        show={showPackagesSuccessModal}
        handleClose={() => {
          setShowPackagesSuccessModal(false);
        }}
        title="Add-On Request Successful"
        text={
          'Your request has been received, you will receive an email with confirmation upon approval.'
        }
        icon={<CheckCircle color="var(--primary-button-color, green)" />}
      />
    </FormProvider>
  );
};
